import React, { useCallback, useMemo } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { Button, useSnackbar } from '@lyearn/ui';
import { useUserContext } from '@/modules/auth/hooks/useUser';
import { NoteReviewStatusTypeEnum } from '@/types/schema';

import { useReviewNoteMutation } from '../../graphql/mutations/generated/reviewNote';
import { useLearnerManagerQuery } from '../../graphql/queries/generated/learnerManager';
import { NoteFragment } from '../../graphql/queries/generated/notes';

function AskForApprovalButton({
  note,
  setCurrentNote,
}: {
  note: NoteFragment;
  setCurrentNote: React.Dispatch<React.SetStateAction<NoteFragment | null>>;
}) {
  const user = useUserContext();
  const [, sendForReview] = useReviewNoteMutation();
  const { showSnackbar, showErrorSnackbar } = useSnackbar();
  const { t } = useTranslation('Coach');

  const [{ data: learnerData }] = useLearnerManagerQuery({
    variables: {
      filters: {
        userId: user?.id,
      },
    },
    pause: !user?.id,
  });

  let managerId = useMemo(() => learnerData?.user?.managerId, [learnerData]);

  const handleClick = useCallback(async () => {
    if (!note.id) {
      console.error('[AskForApprovalButton] note id not found');
      return;
    }
    if (!managerId) {
      if (user?.id) {
        managerId = user.id;
        console.log('[AskForApprovalButton] manager not found. setting reviewer to self');
      } else {
        console.error('[AskForApprovalButton] user id not found');
        return;
      }
    }
    try {
      const response = await sendForReview({
        input: {
          noteId: note.id,
          reviewStatus: NoteReviewStatusTypeEnum.InReview,
          reviewerIds: [managerId],
        },
      });
      if (response?.data?.reviewNote?.success) {
        showSnackbar({ message: 'Note sent for approval!' });
        setCurrentNote(null);
      } else {
        showErrorSnackbar({ message: t('Common:Error.SomeErrorOccurredTryAgain') });
      }
    } catch (error) {
      showErrorSnackbar({ message: t('Common:Error.SomeErrorOccurredTryAgain') });
    }
  }, [sendForReview, managerId, note.id, setCurrentNote]);

  return (
    <Button size="small" onClick={handleClick}>
      {t('Coach:NotesPanel.SendForRevisionTitle')}
    </Button>
  );
}

export default AskForApprovalButton;
